import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import {
  TrendingUp, Wallet, ShoppingBag,
  Utensils, Leaf, Clock,
  Users, RotateCcw, Zap,
  ArrowUp, ArrowDown, CheckCircle2,
} from 'lucide-react';
import { useRegion } from '../../../regions/RegionContext';

// ── KPI data ─────────────────────────────────────────────────────────────────
const kpis = [
  { label: "Revenue Growth Rate", value: 25, icon: TrendingUp,  isReduction: false, signal: "Overall business expansion"      },
  { label: "Profit Growth Rate",  value: 34, icon: Wallet,      isReduction: false, signal: "Real financial improvement"       },
  { label: "Average Order Value", value: 12, icon: ShoppingBag, isReduction: false, signal: "Higher spend per customer"        },
  { label: "Food Cost",           value: 7,  icon: Utensils,    isReduction: true,  signal: "Margin optimization"              },
  { label: "Wastage",             value: 22, icon: Leaf,        isReduction: true,  signal: "Operational control"              },
  { label: "Order Processing",    value: 18, icon: Clock,       isReduction: true,  signal: "Speed & service efficiency"       },
  { label: "Repeat Customers",    value: 24, icon: Users,       isReduction: false, signal: "Improved retention"               },
  { label: "Table Turnover",      value: 20, icon: RotateCcw,   isReduction: false, signal: "Better capacity utilisation"      },
  { label: "Staff Productivity",  value: 15, icon: Zap,         isReduction: false, signal: "Efficiency per employee"          },
];

// ── Benefit copy per KPI ─────────────────────────────────────────────────────
const kpiDetails = {
  "Revenue Growth Rate": {
    headline: "Top-line acceleration",
    points: [
      "Compounds into higher business valuation year over year",
      "Enables reinvestment into staff, menu & marketing",
      "Signals strong operational health to stakeholders",
    ],
  },
  "Profit Growth Rate": {
    headline: "Real margin expansion",
    points: [
      "Every incremental % flows directly to owner take-home",
      "Builds cash reserves for slow seasons & expansion",
      "Reduces dependency on external credit & overdrafts",
    ],
  },
  "Average Order Value": {
    headline: "More revenue per cover",
    points: [
      "Higher guest spend without increasing footfall",
      "Validates upselling systems & menu engineering",
      "Improves ROI on fixed service & labour costs",
    ],
  },
  "Food Cost": {
    headline: "Margins recovered at source",
    points: [
      "Every % saved flows directly to net profit",
      "Tighter recipe costing eliminates invisible leakage",
      "Stronger supplier negotiations backed by real data",
    ],
  },
  "Wastage": {
    headline: "Every rupee protected",
    points: [
      "Direct cost recovery visible from day one",
      "Demand-aligned purchasing reduces over-ordering",
      "Improves kitchen discipline & par-level accuracy",
    ],
  },
  "Order Processing": {
    headline: "Speed becomes revenue",
    points: [
      "Faster service enables more covers per shift",
      "Reduces peak-hour bottlenecks & guest wait times",
      "Lower kitchen stress translates to fewer errors",
    ],
  },
  "Repeat Customers": {
    headline: "Loyalty that compounds",
    points: [
      "Returning guests cost 5× less to serve than new ones",
      "Predictable revenue enables confident planning",
      "Word-of-mouth amplification reduces ad spend",
    ],
  },
  "Table Turnover": {
    headline: "Same seats, more revenue",
    points: [
      "More covers per shift without adding square footage",
      "Optimises kitchen throughput during peak hours",
      "Directly lifts daily & monthly top-line revenue",
    ],
  },
  "Staff Productivity": {
    headline: "Output without overhead",
    points: [
      "More revenue generated per labour rupee spent",
      "Reduces overtime dependency & shift padding",
      "Consistent quality lifts guest satisfaction scores",
    ],
  },
};

// ── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

// ── Animated spring counter ───────────────────────────────────────────────────
function AnimatedCounter({ value, isInView }) {
  // Initial DOM state must show the real value: crawlers and reduced-motion
  // users never scroll the section into view, so a 0 start renders as "0%".
  const motionValue = useMotionValue(value);
  const spring = useSpring(motionValue, { damping: 45, stiffness: 75 });
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    if (!isInView) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    motionValue.jump(0);
    motionValue.set(value);
  }, [isInView, motionValue, value]);
  useEffect(() => spring.on('change', (v) => setDisplay(Math.round(v))), [spring]);
  return <>{display}</>;
}

// ── Mobile-only card ─────────────────────────────────────────────────────────
// Completely separate component built for 3-col ~107px column width on phones.
// No flip — just clean stat display.
function MobileKPICard({ kpi, index, isInView }) {
  const Icon   = kpi.icon;
  const Arrow  = kpi.isReduction ? ArrowDown : ArrowUp;
  const accent = kpi.isReduction ? '#dc2626' : '#1b7840';
  const iconBg = kpi.isReduction ? 'rgba(220,38,38,0.07)' : 'rgba(27,120,64,0.07)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.42, delay: 0.055 * index, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: '#ffffff',
        borderRadius: 10,
        border: `1px solid ${accent}20`,
        borderTop: `3px solid ${accent}`,
        padding: '10px 8px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {/* Icon + badge row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 7 }}>
        <div style={{
          width: 22, height: 22,
          borderRadius: 6,
          background: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon style={{ width: 11, height: 11, color: accent }} strokeWidth={2} />
        </div>
        <span style={{
          fontSize: 6,
          fontWeight: 900,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: accent,
          background: `${accent}10`,
          border: `1px solid ${accent}22`,
          borderRadius: 20,
          padding: '2px 5px',
          lineHeight: 1.25,
          textAlign: 'center',
        }}>
          {kpi.isReduction ? 'Cost\nWin' : 'Growth'}
        </span>
      </div>

      {/* KPI label */}
      <p style={{
        fontSize: 8.5,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        color: '#111827',
        lineHeight: 1.3,
        marginBottom: 8,
        flexGrow: 1,
        wordBreak: 'break-word',
      }}>
        {kpi.label}
      </p>

      {/* Arrow + number */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 8 }}>
        <Arrow style={{ width: 13, height: 13, color: accent, flexShrink: 0 }} strokeWidth={2.5} />
        <div style={{ display: 'flex', alignItems: 'flex-start', lineHeight: 1 }}>
          <span style={{
            fontSize: '1.35rem',
            fontWeight: 900,
            color: accent,
            lineHeight: 1,
            letterSpacing: '-0.03em',
            fontVariantNumeric: 'tabular-nums',
          }}>
            <AnimatedCounter value={kpi.value} isInView={isInView} />
          </span>
          <span style={{ fontSize: '0.58rem', fontWeight: 900, color: `${accent}75`, marginTop: '0.15em' }}>%</span>
        </div>
      </div>

      {/* Signal footer */}
      <div style={{
        borderTop: `1px dashed ${accent}25`,
        paddingTop: 6,
        display: 'flex',
        gap: 4,
        alignItems: 'flex-start',
      }}>
        <div style={{ width: 2, height: 11, borderRadius: 2, background: accent, opacity: 0.4, flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: 7.5, color: '#6b7280', fontStyle: 'italic', lineHeight: 1.3 }}>
          {kpi.signal}
        </span>
      </div>
    </motion.div>
  );
}

// ── Desktop card (flip component) ─────────────────────────────────────────────
// Flip is on Y-axis. Front = KPI stat. Back = Benefits.
// backfaceVisibility:hidden hides each face while it faces away.
function KPICard({ kpi, index, isInView, isMobile }) {
  const [hovered, setHovered] = useState(false);
  const Icon  = kpi.icon;
  const Arrow = kpi.isReduction ? ArrowDown : ArrowUp;

  const green  = '#1b7840';
  const red    = '#dc2626';
  const accent = kpi.isReduction ? red   : green;
  const iconBg = kpi.isReduction
    ? 'rgba(220,38,38,0.08)'
    : 'rgba(27,120,64,0.08)';

  const detail = kpiDetails[kpi.label];

  // Back content rolls in slowly after flip (flip = 0.6s, so delay starts at ~0.5s)
  const rollDelay = (i) => hovered ? 0.5 + i * 0.18 : 0;

  return (
    // Outer: entrance animation + perspective context for 3D flip
    <motion.div
      initial={{ opacity: 0, y: 36, scale: 0.95 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.55, delay: 0.07 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      onClick={() => !isMobile && setHovered(h => !h)}
      style={{ perspective: '1100px', cursor: 'default' }}
    >
      {/* Inner: the element that actually rotates */}
      <motion.div
        animate={{ rotateY: hovered ? 180 : 0 }}
        transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1] }}
        style={{
          transformStyle: 'preserve-3d',
          position: 'relative',
          minHeight: 190,
          borderRadius: 12,
        }}
      >

        {/* ────────────────── FRONT FACE ────────────────── */}
        <div
          className="kpi-front-card"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            borderRadius: 12,
            background: '#f7f8f9',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
            padding: '14px 16px',
            minHeight: 190,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Icon + category pill */}
          <div className="kpi-icon-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="kpi-icon-box" style={{
              width: 34, height: 34,
              borderRadius: 9,
              background: iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon style={{ width: 16, height: 16, color: accent }} strokeWidth={2} />
            </div>
            <span className="kpi-pill" style={{
              fontSize: 8,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: accent,
              background: `${accent}10`,
              border: `1px solid ${accent}22`,
              borderRadius: 20,
              padding: '2px 7px',
            }}>
              {kpi.isReduction ? 'Cost Win' : 'Growth'}
            </span>
          </div>

          {/* KPI label */}
          <p className="kpi-label" style={{
            fontSize: 11,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: '#111827',
            lineHeight: 1.35,
            marginBottom: 10,
            wordBreak: 'break-word',
          }}>
            {kpi.label}
          </p>

          {/* Arrow + number */}
          <div className="kpi-arrow-row" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <motion.div
              animate={{ y: kpi.isReduction ? [0, 4, 0] : [0, -4, 0] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut', repeatType: 'loop' }}
              style={{ flexShrink: 0 }}
            >
              <Arrow style={{ width: 18, height: 18, color: accent }} strokeWidth={2.8} />
            </motion.div>

            <div style={{ display: 'flex', alignItems: 'flex-start', lineHeight: 1 }}>
              <span className="kpi-num" style={{
                fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: 1,
                color: accent,
                fontVariantNumeric: 'tabular-nums',
              }}>
                <AnimatedCounter value={kpi.value} isInView={isInView} />
              </span>
              <span className="kpi-pct" style={{
                fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)',
                fontWeight: 900,
                lineHeight: 1,
                marginTop: '0.2em',
                color: `${accent}75`,
              }}>%</span>
            </div>
          </div>

          {/* Signal footer */}
          <div className="kpi-signal-footer" style={{
            marginTop: 'auto',
            paddingTop: 10,
            borderTop: `1px dashed ${accent}22`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
          }}>
            <div className="kpi-signal-bar" style={{
              width: 3, height: 14,
              borderRadius: 2,
              background: accent,
              opacity: 0.45,
              flexShrink: 0,
              marginTop: 2,
            }} />
            <span className="kpi-signal-text" style={{
              fontSize: 10,
              color: '#6b7280',
              fontStyle: 'italic',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}>
              {kpi.signal}
            </span>
          </div>
        </div>

        {/* ────────────────── BACK FACE ────────────────── */}
        {!isMobile && <div
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            position: 'absolute',
            inset: 0,
            borderRadius: 12,
            background: '#ffffff',
            borderTop: `3px solid ${accent}`,
            border: `1px solid ${accent}22`,
            borderTopWidth: 3,
            boxShadow: `0 8px 28px ${accent}18, 0 2px 8px rgba(0,0,0,0.05)`,
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* "BENEFITS" eyebrow with icon */}
          <motion.div
            animate={hovered ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.38, delay: rollDelay(0) }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 7 }}
          >
            <Zap style={{ width: 11, height: 11, color: accent, flexShrink: 0 }} strokeWidth={0} fill={accent} />
            <span style={{
              fontSize: 9,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.22em',
              color: accent,
            }}>
              Benefits
            </span>
          </motion.div>

          {/* Accent rule */}
          <motion.div
            animate={hovered ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
            transition={{ duration: 0.4, delay: rollDelay(0.5) }}
            style={{
              height: 2, width: 28, borderRadius: 2,
              background: accent, marginBottom: 9,
              transformOrigin: 'left',
            }}
          />

          {/* Detail headline */}
          <motion.p
            animate={hovered ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.42, delay: rollDelay(1) }}
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: '#111827',
              marginBottom: 11,
              lineHeight: 1.3,
            }}
          >
            {detail?.headline}
          </motion.p>

          {/* Bullet points with CheckCircle2 icons — roll in one by one */}
          {detail?.points.map((pt, i) => (
            <motion.div
              key={i}
              animate={hovered ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.48, delay: rollDelay(2 + i) }}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                marginBottom: i < detail.points.length - 1 ? 8 : 0,
              }}
            >
              <CheckCircle2
                style={{ width: 13, height: 13, color: accent, flexShrink: 0, marginTop: 2 }}
                strokeWidth={2.2}
              />
              <p style={{
                fontSize: 11.5,
                color: '#374151',
                lineHeight: 1.5,
                margin: 0,
              }}>
                {pt}
              </p>
            </motion.div>
          ))}
        </div>}
      </motion.div>
    </motion.div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
const MeasurableImprovements = () => {
  const { config } = useRegion();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-60px' });
  const isMobile = useIsMobile();
  const engagementsNote = `Results based on engagements across independent restaurants, QSRs, ${config.terminology.cloudKitchens} & multi-location chains.`;

  // ── Shared divider helpers ──────────────────────────────────────────────────
  const RedDivider = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 1, background: 'rgba(220,38,38,0.2)' }} />
      <motion.span
        initial={{ opacity: 0, scale: 0.85 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.45, delay: 0.5 }}
        style={{
          fontSize: 7,
          fontWeight: 900,
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          color: '#dc2626',
          background: 'rgba(220,38,38,0.05)',
          border: '1px solid rgba(220,38,38,0.2)',
          borderRadius: 20,
          padding: '3px 10px',
          whiteSpace: 'nowrap',
        }}
      >
        Cost &amp; Efficiency Wins
      </motion.span>
      <div style={{ flex: 1, height: 1, background: 'rgba(220,38,38,0.2)' }} />
    </div>
  );

  const GrayDivider = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#d1d5db' }} />
      <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
    </div>
  );

  const mobileGrid = (slice) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {slice.map((kpi, i) => (
        <MobileKPICard key={kpi.label} kpi={kpi} index={i} isInView={isInView} />
      ))}
    </div>
  );

  return (
    <section
      ref={sectionRef}
      className={isMobile ? 'relative overflow-hidden' : 'relative py-16 md:py-20 px-4 md:px-6 overflow-hidden'}
      style={{
        background: 'linear-gradient(160deg, #f0fdf4 0%, #ffffff 50%, #fefce8 100%)',
        ...(isMobile && { padding: '40px 14px 32px' }),
      }}
    >
      {/* Dot pattern */}
      <div className="absolute inset-0 pointer-events-none" style={{
        opacity: 0.022,
        backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
        backgroundSize: '48px 48px',
      }} />

      {/* Ambient blobs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 0.06, scale: 1 }}
        transition={{ duration: 1.4 }} viewport={{ once: true }}
        className="absolute top-10 right-0 w-72 h-72 rounded-full bg-primary blur-3xl pointer-events-none"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 0.05, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.4 }} viewport={{ once: true }}
        className="absolute bottom-10 left-0 w-72 h-72 rounded-full bg-yellow-400 blur-3xl pointer-events-none"
      />

      {isMobile ? (
        /* ─────────────── MOBILE LAYOUT ─────────────── */
        <div style={{ position: 'relative', zIndex: 10 }}>

          {/* Mobile header */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <motion.span
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }} viewport={{ once: true }}
              style={{
                fontSize: 9,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: '#1b7840',
                display: 'block',
                marginBottom: 8,
              }}
            >
              Minimum Guaranteed Results
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true }}
              style={{
                fontSize: '1.45rem',
                fontWeight: 900,
                color: '#111827',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                marginBottom: 6,
              }}
            >
              Measurable Improvements{' '}
              <span style={{ fontStyle: 'italic', fontFamily: 'Georgia, serif', color: '#1b7840' }}>
                — Guaranteed.
              </span>
            </motion.h2>
          </div>

          {/* Mobile cards — 3 groups, 3 cols each */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {mobileGrid(kpis.slice(0, 3))}
            <RedDivider />
            {mobileGrid(kpis.slice(3, 6))}
            <GrayDivider />
            {mobileGrid(kpis.slice(6, 9))}
          </div>

          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }} viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: 18, fontSize: 9, color: '#9ca3af', lineHeight: 1.4 }}
          >
            {engagementsNote}
          </motion.p>
        </div>

      ) : (
        /* ─────────────── DESKTOP LAYOUT (unchanged) ─────────────── */
        <div className="max-w-5xl mx-auto relative z-10">

          {/* Header */}
          <div className="text-center mb-10 md:mb-12">
            <motion.div
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }} viewport={{ once: true }}
              className="inline-block mb-3"
            >
              <span className="text-xs font-bold text-primary uppercase tracking-wider">
                Minimum Guaranteed Results
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.1] mb-4"
              style={{ letterSpacing: '-0.02em' }}
            >
              Measurable Improvements{' '}
              <span className="italic font-serif text-primary">— </span>
              <span className="relative inline-block">
                <span className="italic font-serif text-primary relative z-10">Guaranteed.</span>
                <svg className="absolute -bottom-1.5 left-0 w-full h-2.5" viewBox="0 0 300 10"
                  fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                  <motion.path d="M2 7C75 2, 225 2, 298 7" stroke="#1b7840" strokeWidth="3"
                    strokeLinecap="round" initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }} transition={{ duration: 1, delay: 0.5 }}
                    viewport={{ once: true }} />
                </svg>
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }} viewport={{ once: true }}
              className="text-base md:text-lg text-gray-500 max-w-xl mx-auto"
            >
              Hover any card to unleash the benefits
            </motion.p>
          </div>

          {/* KPI rows */}
          <div className="space-y-3">

            {/* Row 1 — Growth */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {kpis.slice(0, 3).map((kpi, i) => (
                <KPICard key={i} kpi={kpi} index={i} isInView={isInView} isMobile={false} />
              ))}
            </div>

            {/* Reduction row divider */}
            <div className="flex items-center gap-3 py-0.5">
              <div className="flex-1 h-px" style={{ background: 'rgba(220,38,38,0.2)' }} />
              <motion.span
                initial={{ opacity: 0, scale: 0.85 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.45, delay: 0.5 }}
                className="text-[8px] font-black uppercase tracking-[0.22em] px-3 py-1 rounded-full whitespace-nowrap border"
                style={{ background: 'rgba(220,38,38,0.05)', borderColor: 'rgba(220,38,38,0.2)', color: '#dc2626' }}
              >
                Cost &amp; Efficiency Wins
              </motion.span>
              <div className="flex-1 h-px" style={{ background: 'rgba(220,38,38,0.2)' }} />
            </div>

            {/* Row 2 — Reductions */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {kpis.slice(3, 6).map((kpi, i) => (
                <KPICard key={i + 3} kpi={kpi} index={i + 3} isInView={isInView} isMobile={false} />
              ))}
            </div>

            {/* Closing divider */}
            <div className="flex items-center gap-3 py-0.5">
              <div className="flex-1 h-px bg-gray-200" />
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Row 3 — Growth */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {kpis.slice(6, 9).map((kpi, i) => (
                <KPICard key={i + 6} kpi={kpi} index={i + 6} isInView={isInView} isMobile={false} />
              ))}
            </div>

          </div>

          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.6 }} viewport={{ once: true }}
            className="text-center mt-7 text-[10px] text-gray-400"
          >
            {engagementsNote}
          </motion.p>

        </div>
      )}
    </section>
  );
};

export default MeasurableImprovements;
